import React from 'react';
import { Search, LayoutGrid, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

/**
 * SearchAndViewToggle - Reusable component for search bar and card/list view toggle
 * 
 * @param {string} searchQuery - Current search query
 * @param {function} onSearchChange - Callback when search changes
 * @param {string} viewMode - Current view mode ('card' or 'list')
 * @param {function} onViewModeChange - Callback when view mode changes
 * @param {string} placeholder - Search input placeholder
 */
const SearchAndViewToggle = ({ 
  searchQuery, 
  onSearchChange, 
  viewMode = 'card', 
  onViewModeChange,
  placeholder = 'Search...'
}) => {
  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
      {/* Search Bar */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 w-full bg-white border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
          data-testid="search-input"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewModeChange('card')}
          className={`px-3 py-1.5 rounded-md transition-all ${
            viewMode === 'card' 
              ? 'bg-white shadow-sm text-gray-900' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="view-card-btn"
        >
          <LayoutGrid className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewModeChange('list')}
          className={`px-3 py-1.5 rounded-md transition-all ${
            viewMode === 'list' 
              ? 'bg-white shadow-sm text-gray-900' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="view-list-btn"
        >
          <List className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default SearchAndViewToggle;
